/**
 * `mirror` — `MirrorValue` (`specs/M2-tier2-column-types.md` §5.18, `[09§A.7 mirror]`).
 *
 * Verbatim: *"Mirror column values cannot be updated or cleared directly — they reflect the
 * source column's value on the connected board. Filtering mirrored content is not supported."*
 * `value` and `text` are **null on every row** (149/149 sampled); only `display_value` and
 * `mirrored_items` carry anything.
 *
 * **Two serialisations, one store** — the same trap `status` has, and it was a real defect:
 * `displayed_linked_columns` is an **object** in `settings_str` and an **array** in the derived
 * `Column.settings`. Both, verbatim, from board `5939123330` column `mirror9`
 * (`discovery/raw/snapshot-2026-09-10`):
 *
 * ```json
 * // settings_str  (canonical, what we store)
 * {"relation_column": {"subitems": true}, "displayed_column": {},
 *  "displayed_linked_columns": {"5939123543": ["status1"]}, "sumType": "allStatuses"}
 * // Column.settings  (derived, what `column-value-shapes.md` §mirror recorded)
 * {"relation_column": {"subitems": true}, "displayed_column": {},
 *  "displayed_linked_columns": [{"board_id": "5939123543", "column_ids": ["status1"]}], …}
 * ```
 *
 * Only the array form was modelled until round 5r, so every one of the 7 real mirror columns
 * failed `mirrorSettingsSchema` with *"expected array, received object"* — and because
 * `boards { columns { settings_str } }` parses every column's settings, **four of the recorded
 * `boards(page:)` reads came back with GraphQL errors** against the imported database
 * (T-5r; it would have done the same on the live product). The schema now takes either form,
 * the canonical store is the `settings_str` map, and `toMondaySettings` derives the array.
 *
 * `relation_column` names **which relation column on this board** the mirror follows — either a
 * `board_relation` column id or the pseudo-key `subitems`. `displayed_linked_columns` names the
 * column(s) to read on the far side, per board.
 *
 * We **materialise** the reflected values into a `cell_values` row (`materialise: 'always'`) so
 * the grid, sort and group-by have something to read without a cross-board join per cell, while
 * `/v2` still answers `value: null` / `text: null` exactly as monday does
 * (`api: { value: 'null', text: 'null' }`). The worker job `recompute-mirrors` refreshes it
 * when a source cell changes; monday's own note is *"rebuild from the relation column, not from
 * the mirror"* `[cvs§2 mirror]`, which is what the job does.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { asBoardId, asColumnId } from '../../ids.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const mirroredItemSchema = z.looseObject({
    linked_board_id: z.string(),
    linked_item: z.looseObject({ id: z.string(), name: z.string().nullable().optional() }),
    /** The far-side cell, already rendered by *its* own type: `{ text, value?, __typename }`. */
    mirrored_value: z.looseObject({}).nullable().optional(),
});
import { mirrorFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const mirrorValueSchema = z.looseObject({
    display_value: z.string(),
    mirrored_items: z.array(mirroredItemSchema).default([]),
});
/** The `Column.settings` (array) form of `displayed_linked_columns`. */
export const displayedLinkedColumnsArraySchema = z.array(z.looseObject({ board_id: z.string(), column_ids: z.array(z.string()) }));
/**
 * Accepts **both** serialisations and stores the `settings_str` one: a map of far-side board id
 * to the column ids read on it. The array form is what `Column.settings` serves, so an import
 * may hand us either.
 */
export const displayedLinkedColumnsSchema = z
    .union([z.record(z.string(), z.array(z.string())), displayedLinkedColumnsArraySchema])
    .transform((raw) => {
    if (!Array.isArray(raw))
        return raw;
    const map = {};
    for (const entry of raw)
        map[entry.board_id] = [...(map[entry.board_id] ?? []), ...entry.column_ids];
    return map;
});
export const mirrorSettingsSchema = z.looseObject({
    /** `{ "<board_relation column id>": true }` or `{ "subitems": true }` `[cvs§2 mirror]`. */
    relation_column: z.record(z.string(), z.boolean()).optional(),
    displayed_column: z.record(z.string(), z.unknown()).optional(),
    /** Canonical: `{ "<far-side board id>": ["<column id>", …] }`, the `settings_str` form. */
    displayed_linked_columns: displayedLinkedColumnsSchema.optional(),
    /** How several reflected values are folded into one (`allStatuses`, `sum`, …). */
    sumType: z.string().optional(),
    calcType: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
/** The relation this mirror follows: a `board_relation` column id, or subitems. */
export function mirrorRelation(settings) {
    const entries = Object.entries(settings.relation_column ?? {}).filter(([, on]) => on === true);
    const first = entries[0];
    if (first === undefined)
        return null;
    return first[0] === 'subitems' ? { kind: 'subitems' } : { kind: 'column', columnId: first[0] };
}
/** `[boardId, columnId]` pairs this mirror displays from the far side. */
/**
 * `displayed_linked_columns` as the canonical map, whatever form it arrived in.
 *
 * `displayedLinkedColumnsSchema` normalises the array form to the map — but only for settings
 * that went **through** the schema. A caller holding raw `Column.settings` (the array form is
 * what `Column.settings` serves, and it is what the board fixture stores) reached
 * `Object.entries([{board_id, column_ids}])`, whose values are objects, and
 * `columnIds.map(...)` threw `t.map is not a function` — which took the whole route down the
 * moment a mirror cell was opened (`columns-tier2.spec.ts` U2).
 */
function displayedMap(settings) {
    const raw = settings.displayed_linked_columns ?? {};
    if (!Array.isArray(raw))
        return raw;
    const map = {};
    for (const entry of raw) {
        if (typeof entry?.board_id !== 'string' || !Array.isArray(entry.column_ids))
            continue;
        const ids = entry.column_ids.filter((id) => typeof id === 'string');
        map[entry.board_id] = [...(map[entry.board_id] ?? []), ...ids];
    }
    return map;
}
export function mirroredColumns(settings) {
    return Object.entries(displayedMap(settings)).flatMap(([boardId, columnIds]) => columnIds.map((columnId) => ({ boardId, columnId })));
}
/** The derived `Column.settings` array form, in the map's own order. */
export function displayedLinkedColumnsArray(settings) {
    return Object.entries(displayedMap(settings)).map(([boardId, columnIds]) => ({
        board_id: boardId,
        column_ids: [...columnIds],
    }));
}
export const mirrorColumn = defineColumnType({
    type: 'mirror',
    title: 'Mirror',
    description: 'Reflects a column from the connected board. Read-only.',
    apiFragmentName: 'MirrorValue',
    legacyTypeName: 'lookup',
    idPrefix: 'mirror',
    valueSchema: mirrorValueSchema,
    settingsSchema: mirrorSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: mirrorSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    /** *"Filtering mirrored content is not supported"* `[09§A.7 mirror]`. */
    filterConditions: mirrorFilterConditions,
    filterOperators: operatorsOf(mirrorFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    /** P-027: `90397660` `Subitems Status` — monday's footer cell is **empty**. */
    defaultAggregation: 'none',
    emptyText: null,
    computed: {
        dependsOn(column, settings) {
            const parsed = mirrorSettingsSchema.parse(settings ?? {});
            const relation = mirrorRelation(parsed);
            const rows = mirroredColumns(parsed).map((entry) => ({
                kind: 'linked_board',
                boardId: asBoardId(entry.boardId),
                columnId: asColumnId(entry.columnId),
            }));
            if (relation === null)
                return rows;
            return relation.kind === 'subitems'
                ? [...rows, { kind: 'subitems' }]
                : [...rows, { kind: 'column', boardId: column.boardId, columnId: asColumnId(relation.columnId) }];
        },
        compute() {
            /*
             * A mirror cannot be computed from one item's own cells: it needs the linked items on
             * another board and *their* column definitions. `apps/worker/src/jobs/recompute-mirrors.ts`
             * does the join and writes the row; this hook exists for the dependency index.
             */
            return null;
        },
        materialise: 'always',
        /** monday serves neither `value` nor `text` `[cvs§2 mirror]`. */
        api: { value: 'null', text: 'null' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        // *"Mirror column values cannot be updated or cleared directly"* `[09§A.7 mirror]`. The
        // worker writes the materialised row through `recomputeMirrorCell`, not through here.
        throw new ColumnValueError('mirror', 'a mirror reflects the connected board and cannot be written');
    },
    toText() {
        return null;
    },
    toSortKeys(value) {
        // The shadow carries `display_value`, so a mirror column can still be sorted and searched.
        return { text: textShadow(value?.display_value ?? null), number: null, date: null };
    },
    isEqual(a, b) {
        return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { display_value: value.display_value };
    },
    fromMondayValue() {
        // *"Rebuild from the relation column, not from the mirror"* `[cvs§2 mirror]`.
        return null;
    },
    toMondayValue() {
        return null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return mirrorSettingsSchema.parse(raw ?? {});
    },
    /** The **array** form, which is what monday serves as `Column.settings`. */
    toMondaySettings(settings) {
        if (settings.displayed_linked_columns === undefined)
            return { ...settings };
        return { ...settings, displayed_linked_columns: displayedLinkedColumnsArray(settings) };
    },
    /** The **map** form, verbatim: `settings_str` is the canonical store. */
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        return {
            display_value: value?.display_value ?? '',
            mirrored_items: value?.mirrored_items ?? [],
        };
    },
    toWebhookValue() {
        return null;
    },
    /**
     * `text is` compares `display_value` — the same shadow every other mirror row already
     * compares, so the half is declared for completeness and changes nothing.
     */
    compareAttributes: {
        text: {
            shadow: 'text',
            keys: (value) => ({ text: textShadow(value?.display_value ?? null), number: null, date: null }),
        },
    },
    /**
     * **Measured correction (round 5o).** A configured mirror is filterable in monday; the
     * comparison is against `display_value`, which is the `text_value` shadow. An unconfigured
     * mirror has no `display_value`, so the same predicate simply matches nothing.
     */
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map